<template>
  <article
    id="article"
    ref="articleRef"
    class="flex flex-col h-full bg-white relative"
    :class="{
      'text-black': v1Templates,
      'text-semantic-surface-on-surface': !v1Templates
    }"
    :data-testid="dataTestid"
  >
    <div
      id="article-wrapper"
      class="w-full mx-auto"
      :class="component.wrapper"
    >
      <div
        v-if="!!$slots.hero"
        id="article-hero"
        :class="component.hero"
      >
        <slot name="hero" />
      </div>

      <div
        id="article-header"
        :class="component.header"
      >
        <div
          id="article-header-inner"
          :class="layoutMaxWidth"
        >
          <V2MoleculesBreadCrumb
            v-if="!v1Templates"
            :class="component.breadcrumbs"
            :pages="breadcrumbs"
            has-json-schema
          />

          <template v-if="!template.includes('Article3Column')">
            <h1
              v-if="postType === 'Expert Analysis'"
              :class="component.title"
            >
              {{ title }}
            </h1>

            <p
              v-else
              :class="component.title"
            >
              {{ title }}
            </p>
          </template>

          <V2MoleculesAuthorBioBox
            :author="author"
            :editor="editor"
            :tags="tags"
            :date="lastUpdate"
            :class="{
              'mt-8': v1Templates
            }"
          />

          <V2MoleculesShareButtonsLlm
            :post-type="postType"
            :vertical="props.llmType"
          />

          <p
            v-if="_hasSubtitle"
            :class="component.subtitle"
          >
            {{ subTitle }}
          </p>
        </div>
      </div>

      <main
        id="article-main-content"
        class="break-words"
        :class="[
          component.mainContent,
          layoutMaxWidth
        ]"
      >
        <div
          v-if="!template.includes('Article3Column')"
          ref="feature-image-wrapper"
          class="flex flex-col"
          :class="component.featuredImage.wrapper"
        >
          <figure
            v-if="!_isEmpty(featuredImage)"
            :class="component.featuredImage.inner"
          >
            <NuxtImg
              :src="`${featuredImage.url}?fm=webp`"
              :width="featuredImage.width"
              :height="featuredImage.height"
              :alt="title"
              :class="component.featuredImage.image"
              class="w-full"
              format="webp"
              preload
            />

            <figcaption
              v-if="template === 'ArticleLightWithSidebar' && !devices.isMobile.value"
              :class="component.subtitle"
              class="absolute bg-semantic-primary-variant-dark/80 bottom-0 py-3 px-6 hidden md:flex gap-4 items-center w-full"
            >
              <div class="flex-1">
                {{ subTitle }}
              </div>
            </figcaption>
          </figure>

          <p
            v-else-if="featuredImage.description"
            class="mt-2 text text-gray-500 text-xs"
          >
            {{ featuredImage.description }}
          </p>
        </div>

        <div :class="component.mainContentInner">
          <slot name="main-content" />
        </div>

        <slot name="main-content-extra" />
      </main>

      <aside
        v-if="!!$slots['sidebar-left']"
        id="article-sidebar-left"
        ref="sidebarLeftRef"
        :class="component.sidebarLeft"
      >
        <div
          ref="sidebarLeftInnerRef"
          class="article-sidebar-left--inner"
        >
          <slot name="sidebar-left" />
        </div>
      </aside>

      <aside
        v-if="!!$slots['sidebar-right']"
        id="article-sidebar-right"
        ref="sidebarRightRef"
        :class="component.sidebarRight"
      >
        <div>
          <div
            ref="sidebarRightInnerRef"
            class="article-sidebar-right--inner flex flex-col gap-4 md:gap-5 ease-linear w-full"
            :class="[
              layoutMaxWidth,
              {
                'lg:w-[22rem]': template === 'ArticleWithSidebarV2',
                'lg:w-[16.75rem]': template === 'ArticleLightWithSidebar',
                'bg-white': template.includes('Article3Column')
              }
            ]"
          >
            <slot name="sidebar-right" />
            <slot name="sidebar-right-join-club-plus" />
          </div>

          <slot name="sidebar-right-extra" />
        </div>
      </aside>

      <div
        v-if="!!$slots['related-articles']"
        id="article-related-articles"
        :class="[
          component.relatedArticles,
          layoutMaxWidth,
          {
            'pb-10': !$slots.footer
          }
        ]"
      >
        <hr
          v-if="!!$slots['sidebar-right']"
          class="mb-10 border-b border-b-gray-400 border-t-0 border-l-0 border-r-0 lg:hidden"
        />
        <slot name="related-articles" />
      </div>

      <footer
        v-if="!!$slots.footer"
        id="article-footer"
        ref="articleFooterRef"
        class="col-start-1 col-span-full mt-10 empty:hidden"
      >
        <slot name="footer" />
      </footer>
    </div>
  </article>
</template>

<script setup>
import { storeToRefs } from 'pinia'

import _isEmpty from 'underscore/cjs/isEmpty.js'

import {
  TEMPLATES
} from '~/constants/pages/contentful.js'

defineOptions({
  name: 'TemplatesContentfulContent'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: 'contentful-content'
  },

  // TODO: change to theme for consistency
  template: {
    type: String,
    default: '',
    validator: value => TEMPLATES.includes(value)
  },

  title: {
    type: String,
    default: ''
  },

  post: {
    type: Object,
    default: null
  },

  postType: {
    type: String,
    default: 'Guides',
    validator: value => ['Guides', 'Articles', 'Expert Analysis', 'Media'].includes(value)
  },

  subTitle: {
    type: String,
    default: ''
  },

  featuredImage: {
    type: Object,
    default: () => ({})
  },

  lastUpdate: {
    type: String,
    default: ''
  },

  author: {
    type: [String, Object],
    default: () => ({})
  },

  editor: {
    type: [String, Object],
    default: () => ({})
  },

  notFound: {
    type: Boolean,
    default: false
  },

  vertical: {
    type: String,
    default: 'Health Insurance'
  },

  ctaText: {
    type: String,
    default: 'Get free quotes'
  },

  breadcrumbs: {
    type: Array,
    default: () => ([])
  },

  tags: {
    type: Array,
    default: () => ([])
  },

  slug: { // seo? meta?
    type: String,
    default: '/'
  },

  hasShareButtons: {
    type: Boolean,
    default: false
  },

  layoutMaxWidth: {
    type: String,
    default: ''
  },

  llmType: {
    type: String,
    default: ''
  }
})

const $slots = useSlots()
const { devices } = deviceChecker()

const layoutsStore = useLayoutsStore()
const { scrollPosition } = storeToRefs(layoutsStore)

const v1Templates = !['ArticleWithSidebarV2', 'Article3Column', 'Article3Column-D1', 'Article3Column-D2', 'Article3Column-D3'].includes(props.template)

const _hasSubtitle = computed(() => props.subTitle && props.template === 'ArticleWithSidebarV2')

const component = computed(() => {
  const template = props.template

  const wrapper = (() => {
    const grid = getKey(template, {
      default: 'grid grid-cols-contentful-sidebar-v2',
      ArticleLightWithSidebar: 'grid grid-cols-contentful-sidebar-light',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'flex flex-col px-4 md:px-0 md:grid md:grid-cols-contentful-3column-mb lg:grid-cols-contentful-3column-pc'
    })

    const layout = getKey(template, {
      default: '',
      ArticleLightWithSidebar: 'pt-8 lg:pt-14',
      ArticleWithSidebarV2: ['Expert Analysis', 'Media'].includes(props.postType)
        ? 'pt-6'
        : 'pt-5 md:pt-10',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'pt-6 md:pt-0'
    })

    return `${grid} ${layout}`
  })()

  const hero = (() => {
    const grid = getKey(template, {
      default: 'col-span-full',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'col-start-2 col-end-5 md:col-span-full'
    })

    const layout = getKey(template, {
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'mb-6'
    })

    return `row-start-1 ${grid} ${layout}`
  })()

  const header = (() => {
    const grid = getKey(template, {
      ArticleLightWithSidebar: 'row-start-1 col-start-2 col-end-4 lg:col-end-auto',
      ArticleWithSidebarV2: 'col-start-2 col-end-4 lg:col-end-auto',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'col-start-2 col-end-5 lg:col-start-3 lg:col-end-auto lg:row-start-2'
    })

    const margin = getKey(template, {
      ArticleLightWithSidebar: 'mb-7',
      ArticleWithSidebarV2: 'mb-5',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'mb-4 md:mb-6'
    })

    return removeClassExtras(`${grid} ${margin}`)
  })()

  const breadcrumbs = getKey(props.template, {
    default: 'mb-4 md:mb-5',
    'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'mb-4 md:mb-6'
  })

  const title = (() => {
    const text = getKey(props.template, {
      ArticleLightWithSidebar: 'text-xl lg:text-4.5xl font-bold',
      ArticleWithSidebarV2: 'text-2.5xl leading-10 font-bold md:text-4xl lg:text-4.5xl lg:leading-12'
    })

    const margin = !v1Templates || _hasSubtitle.value 
      ? getKey(props.template, {
        ArticleLightWithSidebar: 'mb-7',
        ArticleWithSidebarV2: 'mb-2 lg:mb-4'
      })
      : ''

    return `leading-tight ${text} ${margin}`
  })()

  const subtitle = getKey(template, {
    ArticleLightWithSidebar: 'text-white font-bold text-lg',
    ArticleWithSidebarV2: 'text-base lg:text-lg leading-tight'
  })

  const featuredImage = {
    wrapper: getKey(template, {
      ArticleLightWithSidebar: 'gap-y-6',
      ArticleWithSidebarV2: 'mb-4 md:mb-10'
    }),
    inner: getKey(template, {
      ArticleLightWithSidebar: 'relative lg:shadow-md rounded-sm'
    }),
    image: getKey(template, {
      ArticleWithSidebarV2: `w-full object-contain rounded-2.5xl ${getKey(props.postType, {
        default: 'h-[11.0625rem] md:h-[14.5625rem] lg:h-[340px]',
        'Expert Analysis': 'h-[177px] md:h-auto'
      })}`
    })
  }

  const mainContent = (() => {
    const grid = getKey(template, {
      default: 'col-start-2 col-span-2',
      ArticleLightWithSidebar: 'col-start-2 col-end-4 lg:col-end-auto',
      ArticleWithSidebarV2: 'col-start-2 col-end-4 lg:col-end-auto',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'max-w-full md:max-w-none md:col-start-2 md:col-end-5 lg:col-start-3 lg:col-end-auto lg:row-start-3'
    })

    return `${grid}`
  })()

  const mainContentInner = (() => {
    const className = (() => {
      if (props.template.includes('Article3Column')) {
        return 'article-3column-content'
      }

      return v1Templates
        ? 'post-content'
        : 'article-content'
    })()

    const padding = getKey(props.template, {
      default: 'pb-7',
      ArticleLightWithSidebar: 'lg:pb-12',
      ArticleWithSidebarV2: 'pb-10 lg:pb-0',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'pb-6 lg:pb-10'
    })

    return removeClassExtras(`${className} ${padding}`)
  })()

  const sidebarLeft = (() => {
    const grid = getKey(template, {
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'col-start-2 row-start-2 row-span-3'
    })

    const margin = getKey(template, {
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'lg:pr-9 lg:pt-[43px]'
    })

    return `${grid} ${margin}`
  })()

  const sidebarRight = (() => {
    const grid = getKey(template, {
      default: 'row-start-3 col-start-1 col-span-full lg:row-start-1 lg:row-span-3 lg:col-start-3 lg:col-end-4',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'col-start-4 row-start-2 row-span-3'
    })

    const layout = getKey(template, {
      ArticleLightWithSidebar: 'pb-8 lg:pb-14',
      ArticleWithSidebarV2: ['Expert Analysis', 'Media'].includes(props.postType)
        ? 'mb-10 lg:mb-0 lg:pt-16'
        : 'pb-[4.1875rem] lg:pt-10'
    })

    const margin = getKey(template, {
      default: 'px-4 lg:pl-8 lg:pr-0',
      'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'lg:pl-9 lg:pt-[2.6875rem]'
    })

    return removeClassExtras(`relative ${margin} ${grid} ${layout}`)
  })()

  const relatedArticles = getKey(template, {
    default: 'flex flex-col col-start-2 col-span-2 lg:col-end-2',
    ArticleWithSidebarV2: 'flex flex-col col-start-2 col-span-2 lg:col-end-2 lg:pt-10',
    'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 'flex flex-col col-start-2 col-end-5 lg:col-start-3 lg:col-end-auto'
  })

  return {
    wrapper,
    hero,
    header,
    breadcrumbs,
    title,
    subtitle,
    featuredImage,
    mainContent,
    mainContentInner,
    sidebarLeft,
    sidebarRight,
    relatedArticles
  }
})

const sidebarInnerBaseOffset = getKey(props.template, {
  ArticleWithSidebarV2: 144,
  'Article3Column, Article3Column-D1, Article3Column-D2, Article3Column-D3': 391.66668701171875,
  ArticleLightWithSidebar: 120
})

const sidebarRightRef = ref()
const sidebarRightInnerRef = ref()

const sidebarLeftRef = ref()
const sidebarLeftInnerRef = ref()

const sidebarSticky = ref({
  right: false,
  left: false
})
const stickToBottom = ref({
  right: false,
  left: false
})

watch(scrollPosition, val => devices.breakpoint('lg') && handleScroll(val))

watch(devices.width, () => {
  if (!devices.breakpoint('lg')) {
    if (sidebarRightInnerRef.value) {
      sidebarRightInnerRef.value.style.position = 'relative'
    }

    if (sidebarLeftInnerRef.value) {
      sidebarLeftInnerRef.value.style.position = 'relative'
    }
  }
})

function handleScroll (scrollPos) {
  if (props.postType === 'Expert Analysis' || props.template.includes('Article3Column')) {
    return
  }

  const sidebarRight = sidebarRightRef.value && sidebarRightInnerRef.value
    ? {
      main: sidebarRightRef.value,
      inner: sidebarRightInnerRef.value
    }
    : null

  const sidebarLeft = sidebarLeftRef.value && sidebarLeftInnerRef.value
    ? {
      main: sidebarLeftRef.value,
      inner: sidebarLeftInnerRef.value
    }
    : null

  const sidebars = {
    right: sidebarRight,
    left: sidebarLeft
  }
  const sidebarKeys = Object.keys(sidebars)

  if (_isEmpty(sidebars.right) && _isEmpty(sidebars.left)) {
    return
  }

  for (let i = 0; i < sidebarKeys.length; i++) {
    const key = sidebarKeys[i]
    const sidebar = sidebars?.[key]

    if (_isEmpty(sidebar)) {
      continue
    }

    const mainRect = sidebar?.main.getBoundingClientRect()
    const innerRect = sidebar?.inner.getBoundingClientRect()

    const sidebarHeight = mainRect?.height || 0
    const innerHeight = innerRect?.height || 0

    const newOffset = scrollPos + innerHeight

    sidebarSticky.value[key] = scrollPos >= sidebarInnerBaseOffset
    stickToBottom.value[key] = newOffset >= (sidebarHeight + 40)

    if (
      (sidebarSticky.value.left && stickToBottom.value.left) ||
      (sidebarSticky.value.right && stickToBottom.value.right)
    ) {
      if (sidebars?.left) {
        sidebars.left.inner.style.position = 'absolute'

        sidebars.left.inner.style.top = ''
        sidebars.left.inner.style.paddingTop = ''
        sidebars.left.inner.style.bottom = '25px'
      }

      if (sidebars?.right) {
        sidebars.right.inner.style.position = 'absolute'

        sidebars.right.inner.style.top = ''
        sidebars.right.inner.style.paddingTop = ''
        sidebars.right.inner.style.bottom = '25px'
      }
    } else if (sidebarSticky.value[key] && !stickToBottom.value[key]) {
      sidebar.inner.style.position = 'fixed'

      sidebar.inner.style.top = '0'
      sidebar.inner.style.paddingTop = '10px'
      sidebar.inner.style.bottom = ''
    } else if (!sidebarSticky.value[key]) {
      sidebar.inner.style.position = 'relative'

      sidebar.inner.style.top = ''
      sidebar.inner.style.paddingTop = ''
      sidebar.inner.style.bottom = ''
    }
  }
}
</script>

<style>

.image-caption {
  background-color: rgba(22, 60, 96, 0.8);
}

.post-content {
  @apply text-black;
  word-break: break-word;
}
.post-content h1,
.post-content h2,
.post-content h3,
.post-content h4,
.post-content h5,
.post-content h6 {
  @apply font-bold;
}
.post-content h1 {
  @apply text-4.5xl;
}
.post-content h2 {
  @apply text-3.5xl my-6;
}
.post-content h3 {
  @apply text-2xl my-6;
}
.post-content > p {
  @apply text-lg my-6;
}
.post-content ul li {
  @apply relative flex mb-4;
}
.post-content ul li:before {
  content: "";
  @apply inline-block mr-4 relative top-2 rounded-full bg-primary;
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
}
.post-content ul li > * {
  margin: 0;
}
.post-content ul li > p {
  @apply text-lg;
}
.post-content a:not([class]) {
  @apply underline break-all;
}
.post-content blockquote {
  @apply bg-gray-10 rounded-lg border border-gray-400 p-4 lg:p-5;
}
.post-content > ul,
.post-content > ol {
  @apply pl-4;
}
.post-content > ol {
  @apply list-decimal;
}
.post-content .article-calculator:not(:last-child) {
  margin-bottom: 1.5rem;
}

.article-content,
.article-3column-content {
  word-break: break-word;
}
.article-content > div,
.article-3column-content > div {
  @apply mt-8;
}
.article-content h1,
.article-content h2,
.article-content h3,
.article-content h4,
.article-content h5,
.article-content h6,
.article-3column-content h1,
.article-3column-content h2,
.article-3column-content h3,
.article-3column-content h4,
.article-3column-content h5,
.article-3column-content h6 {
  @apply font-bold mt-6 md:mt-10;
}
.article-content h1:not(:last-child),
.article-content h2:not(:last-child),
.article-content h3:not(:last-child),
.article-content h4:not(:last-child),
.article-content h5:not(:last-child),
.article-content h6:not(:last-child),
.article-content table:not(:last-child),
.article-content .article-calculator:not(:last-child),
.article-3column-content h1:not(:last-child),
.article-3column-content h2:not(:last-child),
.article-3column-content h3:not(:last-child),
.article-3column-content h4:not(:last-child),
.article-3column-content h5:not(:last-child),
.article-3column-content h6:not(:last-child),
.article-3column-content table:not(:last-child),
.article-3column-content .article-calculator:not(:last-child) {
  @apply mb-4;
}
.article-content > p,
.article-3column-content > p {
  @apply lg:text-lg lg:leading-[1.625rem];
}
.article-content > ul > li > p,
.article-3column-content > ul > li > p {
  @apply lg:text-lg lg:leading-[1.625rem];
}
.article-content blockquote,
.article-3column-content blockquote {
  @apply bg-gray-10 rounded-lg border border-gray-400 p-4 lg:p-5 mb-4;
}
.article-content blockquote > p:not(:last-child),
.article-3column-content blockquote > p:not(:last-child) {
  @apply mb-4;
}
.article-content > ul:not(.molecules-share-buttons),
.article-content > ol,
.article-3column-content > ul:not(.molecules-share-buttons),
.article-3column-content > ol {
  @apply pl-4;
}
.article-content > ol,
.article-3column-content > ol {
  @apply list-decimal;
}
.article-content > ul:not(.molecules-share-buttons),
.article-3column-content > ul:not(.molecules-share-buttons) {
  @apply list-disc ml-4;
}
.article-content > ul:not(.molecules-share-buttons):not(:last-child),
.article-3column-content > ul:not(.molecules-share-buttons):not(:last-child) {
  @apply mb-5 lg:mb-[1.625rem];
}
.article-content > ul:not(.molecules-share-buttons) ul,
.article-3column-content > ul:not(.molecules-share-buttons) ul {
  @apply list-[circle] ml-4;
}
.article-content a:not([class]),
.article-3column-content a:not([class]) {
  @apply underline break-words;
}

.article-content > p:not(:last-of-type) {
  @apply mb-5 lg:mb-[1.625rem];
}
.article-content h1 {
  @apply text-4xl md:text-4.5xl;
}
.article-content h2 {
  @apply text-2.5xl md:text-4xl;
}
.article-content h3 {
  @apply text-2xl md:text-2.5xl;
}
.article-content h4 {
  @apply text-xl md:text-2xl;
}
.article-content h5 {
  @apply text-base md:text-xl;
}
.article-content h6 {
  @apply text-base;
}

.article-3column-content > p:not(:last-of-type) {
  @apply mb-6 lg:mb-[1.875rem];
}
.article-3column-content {
  h1, h2, h3, h4, h5, h6 {
    &:first-of-type {
      @apply mt-0 !important;
    }
  }
}
.article-3column-content h1 {
  @apply text-3.5xl md:text-4.5xl mb-6 last:mb-0;
}
.article-3column-content h2 {
  @apply text-2xl md:text-3.5xl mb-4;
}
.article-3column-content h3 {
  @apply text-1.5xl md:text-2.5xl mb-4;
}
.article-3column-content h4 {
  @apply text-xl md:text-2xl mb-4;
}
.article-3column-content h5 {
  @apply text-lg/[1.625rem] md:text-xl mb-4;
}
.article-3column-content h6 {
  @apply mb-4;
}

.article-content table {
  @apply max-w-[46rem];
}
.article-3column-content table {
  @apply max-w-[40.875rem];
}

.post-content table,
.article-content table,
.article-3column-content table {
  @apply text-sm border-separate border-spacing-0 block overflow-auto;
}
.post-content table td,
.article-content table td,
.article-3column-content table td {
  @apply border-gray-400 border-b-1 border-l-1 last:border-r-1 px-3 py-3 font-bold;
}
.post-content table td p,
.article-content table td p,
.article-3column-content table td p {
  @apply m-0;
}
.post-content table tr:first-child th,
.post-content table tr:first-child td,
.article-content table tr:first-child th,
.article-content table tr:first-child td,
.article-3column-content table tr:first-child th,
.article-3column-content table tr:first-child td {
  @apply bg-blue-10 py-[0.875rem] px-3 border-gray-400 first:rounded-tl-2xl last:rounded-tr-2xl text-left font-bold border-b-1 border-l-1 border-t-1 last:border-r-1;
}
.post-content table tr:nth-child(odd) td,
.article-content table tr:nth-child(odd) td,
.article-3column-content table tr:nth-child(odd) td {
  @apply bg-blue-5;
}
.post-content table tr:last-child td:first-child,
.article-content table tr:last-child td:first-child,
.article-3column-content table tr:last-child td:first-child {
  @apply first:rounded-bl-2xl;
}
.post-content table tr:last-child td:last-child,
.article-content table tr:last-child td:last-child,
.article-3column-content table tr:last-child td:last-child {
  @apply last:rounded-br-2xl;
}

.atoms-accordion ul:not(.molecules-share-buttons),
.atoms-accordion ol {
  @apply pl-4;
}
.atoms-accordion ol {
  @apply list-decimal;
}
.atoms-accordion ul:not(.molecules-share-buttons) {
  @apply list-disc ml-4;
}
.atoms-accordion ul:not(.molecules-share-buttons):not(:last-child) {
  @apply mb-5 lg:mb-[1.625rem];
}

.article-content table tr:first-child td:first-child {
  @apply bg-base-lavender-300;
}
.article-content table tr:first-child td:nth-child(2) {
  @apply bg-base-lavender-200;
}
.article-content table tr:first-child td:nth-child(3) {
  @apply bg-base-lavender-100;
}
.article-content table tr:first-child td:nth-child(4) {
  @apply bg-base-lavender-50;
}

.article-3column-content table tr:first-child th:first-child {
  @apply bg-base-lavender-300;
}
.article-3column-content table tr:first-child th:nth-child(2) {
  @apply bg-base-lavender-200;
}
.article-3column-content table tr:first-child th:nth-child(3) {
  @apply bg-base-lavender-100;
}
.article-3column-content table tr:first-child th:nth-child(4) {
  @apply bg-base-lavender-50;
}

</style>
